import React from "react";
import { Box, Typography } from "@material-ui/core";
import clsx from "clsx";
import dayjs from "dayjs";
import { RouteComponentProps } from "react-router-dom";

import {
  BannerSeverity,
  Icon,
  IconName,
  MyTripsMobileCard,
  NotificationBanner,
  ActionLinks,
  IActionLink,
} from "halifax";
import {
  MyTripsFilter,
  ExperienceBookingStatusEnum,
  ExperienceReservation,
  ItineraryEnum,
} from "redmond";
import { useIsTravelApp } from "@capone/common";

import * as textConstants from "./constants";
import { ExperienceCardConnectorProps } from "./container";
import { DesktopExperienceCard } from "./components";
import { buildExploreActivitiesActions } from "../../../../../../utils/activitiesHelpers";
import "./styles.scss";

const getStatusBanner = (bookingStatus: ExperienceBookingStatusEnum) => {
  switch (bookingStatus) {
    case ExperienceBookingStatusEnum.Cancelled:
      return (
        <NotificationBanner
          className="status-banner"
          label={textConstants.CANCELED_RES}
          severity={BannerSeverity.ERROR}
          icon={<Icon name={IconName.ErrorAlert} />}
        />
      );
    default:
      return undefined;
  }
};

export interface IExperienceCardProps
  extends ExperienceCardConnectorProps,
  RouteComponentProps {
  isMobile?: boolean;
  experienceReservation: ExperienceReservation;
  expandedCard: string;
  onExpandCard: (cardId: string) => void;
  showActivitiesXSell?: boolean;
}

export const ExperienceCard = (props: IExperienceCardProps) => {
  const {
    history,
    experienceReservation,
    isMobile,
    populateTripQueryParams,
    onExpandCard,
    expandedCard,
    setOpenModal,
    tripsFilter,
    setSelectedExperience,
    showActivitiesXSell = false,
  } = props;

  const { isTravelApp } = useIsTravelApp();

  const isPastTrips = tripsFilter === MyTripsFilter.PAST_TRIPS;

  const {
    experienceSelection: { travelDate },
    status,
    bookingDetails,
  } = experienceReservation.reservation;

  const statusBanner = getStatusBanner(status);
  const isCancel = status === ExperienceBookingStatusEnum.Cancelled;
  const confirmationCodeClassName = isCancel ? "warning" : "";

  const travelerNames = textConstants.getTravelerNames(bookingDetails);

  const cityLabel = experienceReservation.reservation.experienceDetails?.destination;
  const moreActions: IActionLink[] = showActivitiesXSell && cityLabel
    ? buildExploreActivitiesActions({
      cityLabel,
      fallbackLabel: cityLabel,
      coords: experienceReservation.reservation.experienceDetails?.logistics?.startLocations?.[0]?.coordinates,
      fromDate: travelDate,
      untilDate: dayjs(travelDate).add(7, "day").format("YYYY-MM-DD"),
      isTravelApp,
      history,
      product: ItineraryEnum.Experience,
    })
    : [];

  const experienceInfoDetails = textConstants.getExperienceInfoDetails(experienceReservation);

  return (
    <Box className={clsx({ mobile: isMobile }, "experience-list")}>
      {isMobile ? (
        <MyTripsMobileCard
          onClick={() => {
            setSelectedExperience(experienceReservation);
            populateTripQueryParams(history);
          }}
          banner={statusBanner}
          confirmationCodeClassName={confirmationCodeClassName}
          label={textConstants.EXPERIENCE_HEADER_TEXT}
          icon={IconName.ExperiencesIcon}
          title={experienceInfoDetails.title ?? ""}
          confirmationLabel={experienceInfoDetails.confirmationLabel}
          confirmationCode={experienceInfoDetails.confirmationCode}
          startDate={experienceInfoDetails.startDate}
          endDate={experienceInfoDetails.endDate}
          bottomContent={
            moreActions.length > 0
              ? <ActionLinks className="trip-info-bottom-content" actions={moreActions} />
              : undefined
          }
        />
      ) : (
        <div>
          <Box className="experience-card-icon-title">
            <div className="experience-icon-wrapper">
              <Icon name={IconName.ExperiencesIcon} />
            </div>
            <Typography variant="body1" className="summary-label">
              {textConstants.getHeaderText(travelDate)}
            </Typography>
          </Box>
          <DesktopExperienceCard
            banner={statusBanner}
            isPastTrips={isPastTrips}
            experienceReservation={experienceReservation}
            isCancel={isCancel}
            confirmationCodeClassName={confirmationCodeClassName}
            isMobile={isMobile}
            moreActions={moreActions}
            expandedCard={expandedCard}
            onExpandCard={onExpandCard}
            setOpenModal={setOpenModal}
            travelerNames={travelerNames}
          />
        </div>
      )}
    </Box>
  );
};
